import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Users, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import UserSearchSelect, { SelectedUser } from '../UserSearchSelect';
import { useGroupActivityStore } from '@/store/groupActivityStore';
import { GroupActivity } from '@/types/groupActivity';

interface InviteToGroupActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: GroupActivity;
}

const InviteToGroupActivityModal: React.FC<InviteToGroupActivityModalProps> = ({
  isOpen,
  onClose,
  activity
}) => {
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { inviteToGroupActivity } = useGroupActivityStore();

  const handleInvite = async () => {
    if (selectedUsers.length === 0) return;
    
    setIsSubmitting(true);
    try {
      // Convert selected users to array of auth_ids
      const userIds = selectedUsers.map(user => user.auth_id);
      
      // Invite users to the activity
      await inviteToGroupActivity(activity.id, userIds);
      
      // Show success message
      setShowSuccess(true);
      
      // Reset form after a delay
      setTimeout(() => {
        setSelectedUsers([]);
        setShowSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error inviting users to activity:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-dark-200 text-white border-dark-300">
        {showSuccess ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-center mb-2">
              Invitations Sent!
            </h2>
            <p className="text-center text-dark-800 mb-6">
              {selectedUsers.length} {selectedUsers.length === 1 ? 'member has' : 'members have'} been invited to join this activity.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-gold-400" />
                <span>Invite Members</span>
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">{activity.title}</h3>
                <p className="text-dark-800 mb-4">
                  {activity.sport} • {activity.groupDate} • {activity.groupTime}
                </p>
                <div className="bg-dark-300 p-4 rounded-lg mb-4">
                  <p className="text-sm text-dark-800">
                    Invite members to join this group activity. They will receive a notification and can choose to accept or decline.
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <label className="block text-sm font-medium text-dark-800 mb-1">
                  Search and select members to invite:
                </label>
                <UserSearchSelect
                  onSelect={setSelectedUsers}
                  selectedUsers={selectedUsers}
                  placeholder="Search members by name..."
                  maxUsers={10}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={onClose}
                className="border-dark-400"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleInvite}
                disabled={selectedUsers.length === 0 || isSubmitting}
                className="bg-gold-400 hover:bg-gold-500 text-dark-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending Invites...
                  </>
                ) : (
                  `Invite ${selectedUsers.length > 0 ? selectedUsers.length : ''} ${selectedUsers.length === 1 ? 'Member' : 'Members'}`
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteToGroupActivityModal;